import { useTranslation } from "react-i18next";
import { type OptionDto } from "../../../../../../autogen/bff-api";
import { useDisplayDate } from "../../../../../../common/formatting/useDisplayDate";
import { refusedFieldErrors } from "../../../../../../common/redux/reducers/contractReducer";
import { type EditContractCommandType } from "../../../../../../common/redux/thunks/contract/edit-contract-thunk";
import { useExtensionName } from "../../../../options/extensionName";
import { type LapseAtEndDate } from "../../../../options/unsavedOptionPeriods";
import { useContractState } from "../../../useContractState";

export const WOULD_LAPSE = "zz_ContractApiErrorCode_OptionWouldLapse";

interface WouldLapse {
  optionId?: string;
  name?: string | null;
  declinedOptionId?: string;
  declinedName?: string | null;
  declinedStart?: string | null;
}

export const useWouldLapseExplanation = (type: EditContractCommandType, held: LapseAtEndDate | undefined) => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const contractState = useContractState();
  const options = contractState.contract.dataFields.options ?? [];

  const extensionName = useExtensionName(options);

  const nameOf = (id: string | undefined, name: string | null | undefined) =>
    name?.trim() || extensionName(options.find((option) => option.id === id) ?? ({ id } as OptionDto));

  const explain = (lapse: WouldLapse) => {
    const name = nameOf(lapse.optionId, lapse.name);
    const declined = nameOf(lapse.declinedOptionId, lapse.declinedName);
    return lapse.declinedStart
      ? t(
          "{{name}} would then run into {{declined}}, which was not exercised. Choose an end date that makes {{name}} end before {{start}}.",
          { name, declined, start: displayDate(lapse.declinedStart) }
        )
      : t("{{name}} was not exercised, so this extension would lapse. It is not saved.", { name: declined });
  };

  if (held) {
    return explain({
      optionId: held.option.id,
      name: held.option.name,
      declinedOptionId: held.declined.id,
      declinedName: held.declined.name,
      declinedStart: held.declined.startDate,
    });
  }
  const refusals = refusedFieldErrors(contractState, type).filter((error) => error.code === WOULD_LAPSE);
  if (refusals.length === 0) return null;
  return refusals.map((refusal) => explain(refusal.params ?? {})).join(" ");
};
